import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
    FaBook, FaFolderPlus, FaPlus, FaStar, FaThumbtack, FaPen, FaArchive,
    FaBookOpen, FaFileAlt, FaChartBar, FaSlidersH,
} from "react-icons/fa";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { EmptyState, Skeleton } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { onApiAction } from "../../../../_UI/onApiAction";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { NOTEBOOK_COLLECTION_CONSTANTS, NOTEBOOK_ENTRY_CONSTANTS } from "../_MODULES/_API";
import { NotebookCollectionModal } from "../_UI/NotebookCollectionModal";
import { NotebookEntryFormModal, NOTEBOOK_TYPES, NOTEBOOK_STATUSES } from "../_UI/NotebookEntryFormModal";
import { NotebookCollectionWorkspaceModal } from "../_UI/NotebookCollectionWorkspaceModal";
import {
    NotebookAdvancedFilterPanel,
    NOTEBOOK_ADVANCED_FILTER_EMPTY,
    cloneNotebookAdvancedFilters,
    onNotebookAdvancedFilterCount,
} from "../_UI/NotebookAdvancedFilterModal";
import "../_UI/_Styles/Notebook.css";

const labelOf = (opts, value) => opts.find((o) => o.value === value)?.label || value || "—";
const plain = (html = "") => DOMPurify.sanitize(String(html), { ALLOWED_TAGS: [] }).replace(/&nbsp;/g, " ").trim();
const cloneAdvanced = (value = NOTEBOOK_ADVANCED_FILTER_EMPTY) => cloneNotebookAdvancedFilters(value);

const entrySearchParts = (entry, collectionMap) => [
    entry.title, entry.subtitle, entry.summary, plain(entry.content), entry.author, entry.language,
    entry.genre, entry.mood, entry.source, entry.musicalKey, entry.bpm, entry.timeSignature, entry.tuning,
    entry.capo, entry.versionLabel, entry.bookTitle, entry.chapterNumber, entry.sectionName,
    collectionMap.get(String(entry.collectionId))?.name,
    ...(entry.tags || []),
    ...(entry.referenceLinks || []).flatMap((row) => [row?.label, row?.url]),
    ...(entry.customFields || []).flatMap((row) => [row?.label, row?.value]),
];

const onMatchesAdvanced = (entry, filters) => {
    if (filters.collectionId !== "ALL") {
        if (filters.collectionId === "NONE" ? !!entry.collectionId : String(entry.collectionId) !== String(filters.collectionId)) return false;
    }
    if (filters.type !== "ALL" && entry.type !== filters.type) return false;
    if (filters.status !== "ALL" && entry.status !== filters.status) return false;
    if (filters.isFavorite !== "ALL" && (!!entry.isFavorite !== (filters.isFavorite === "YES"))) return false;
    if (String(filters.author || "").trim() && !onMatchesSearch(filters.author, [entry.author])) return false;
    if (String(filters.tag || "").trim() && !onMatchesSearch(filters.tag, entry.tags || [])) return false;
    const dateValue = filters.dateField === "CREATED" ? entry.createdAt : entry.updatedAt;
    return onDateMatches(dateValue, filters.dateRange, filters.dateExact);
};

export const NotebookViewMain = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [advancedFilters, setAdvancedFilters] = useState(() => cloneAdvanced());
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [collectionModal, setCollectionModal] = useState(false);
    const [editingCollection, setEditingCollection] = useState(null);
    const [entryModal, setEntryModal] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [workspaceCollection, setWorkspaceCollection] = useState(null);
    const [workspaceEntryId, setWorkspaceEntryId] = useState(null);
    const [returnCollectionId, setReturnCollectionId] = useState(null);
    const dialogClosingRef = useRef(false);

    const CQ = useApiQuery({ minTimeQuery: 180, enablePagination: false, startLoading: true });
    const EQ = useApiQuery({ minTimeQuery: 180, enablePagination: false, startLoading: true });
    const load = () => { CQ.apiGet({ endpoint: NOTEBOOK_COLLECTION_CONSTANTS.LIST }); EQ.apiGet({ endpoint: NOTEBOOK_ENTRY_CONSTANTS.LIST }); };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const collections = Array.isArray(CQ.useApiData) ? CQ.useApiData : [];
    const entries = Array.isArray(EQ.useApiData) ? EQ.useApiData : [];
    const collectionMap = useMemo(() => new Map(collections.map((c) => [String(c.id), c])), [collections]);
    const advancedCount = useMemo(() => onNotebookAdvancedFilterCount(advancedFilters), [advancedFilters]);
    const hasActiveFiltering = !!search.trim() || advancedCount > 0;

    const visible = useMemo(() => entries
        .filter((entry) => onMatchesAdvanced(entry, advancedFilters))
        .filter((entry) => onMatchesSearch(search, entrySearchParts(entry, collectionMap))),
    [entries, advancedFilters, search, collectionMap]);

    const ungroupedCollection = useMemo(() => ({
        id: "",
        name: "Sin colección",
        description: "",
        color: "#777",
        isVirtual: true,
    }), []);

    const collectionBuckets = useMemo(() => {
        const sourceEntries = hasActiveFiltering ? visible : entries;
        const ungroupedCount = sourceEntries.filter((entry) => !entry.collectionId).length;
        const buckets = [];
        // Lo pendiente de clasificar siempre va primero para que no quede escondido entre colecciones.
        if (ungroupedCount > 0) buckets.push({ collection: ungroupedCollection, count: ungroupedCount });
        buckets.push(...collections.map((collection) => ({
            collection,
            count: sourceEntries.filter((entry) => String(entry.collectionId || "") === String(collection.id)).length,
        })).filter((bucket) => !hasActiveFiltering || bucket.count > 0));
        return buckets;
    }, [collections, entries, visible, hasActiveFiltering, ungroupedCollection]);

    useEffect(() => {
        const routed = searchParams.get("collection");
        if (!routed) {
            setWorkspaceCollection(null);
            setWorkspaceEntryId(null);
            return;
        }
        const target = routed === "NONE"
            ? ungroupedCollection
            : collections.find((collection) => String(collection.id) === String(routed));
        if (!target) return;
        setWorkspaceCollection((current) => {
            const currentKey = current?.isVirtual ? "NONE" : String(current?.id || "");
            return currentKey === String(routed) ? current : target;
        });
    }, [searchParams, collections, ungroupedCollection]);

    useEffect(() => {
        const dialog = searchParams.get("dialog");
        if (dialogClosingRef.current) {
            if (!dialog) dialogClosingRef.current = false;
            return;
        }
        if (!dialog) return;
        if (dialog === "collection-new") { setEditingCollection(null); setCollectionModal(true); return; }
        if (dialog.startsWith("collection:")) {
            const id = dialog.slice("collection:".length);
            const collection = collections.find((row) => String(row.id) === String(id));
            if (collection) { setEditingCollection(collection); setCollectionModal(true); }
            return;
        }
        if (dialog === "entry-new" || dialog.startsWith("entry-new:")) {
            const routedCollection = dialog.includes(":") ? dialog.slice("entry-new:".length) : searchParams.get("collection");
            setEditingEntry(null);
            setReturnCollectionId(routedCollection === "NONE" ? "__NONE__" : (routedCollection || null));
            setEntryModal(true);
            return;
        }
        if (dialog.startsWith("entry:")) {
            const id = dialog.slice("entry:".length);
            const entry = entries.find((row) => String(row.id) === String(id));
            if (!entry) return;
            const routedCollection = searchParams.get("collection");
            setEditingEntry(entry);
            setReturnCollectionId(routedCollection === "NONE" ? "__NONE__" : (routedCollection || entry.collectionId || null));
            setEntryModal(true);
        }
    }, [searchParams, collections, entries]);

    const updateRouteState = (patch = {}) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    const openWorkspace = (collection, entryId = null) => {
        setWorkspaceEntryId(entryId || null);
        setWorkspaceCollection(collection);
        updateRouteState({ collection: collection?.isVirtual ? "NONE" : collection?.id, entry: entryId || null, preview: null, dialog: null });
    };

    const closeWorkspace = () => {
        // La URL manda el estado del workspace. Como el efecto de ruta ya no depende
        // del estado local, una sola X alcanza: no puede reabrirse con la URL anterior.
        updateRouteState({ collection: null, entry: null, preview: null, entryAdvanced: null, dialog: null });
        setWorkspaceCollection(null);
        setWorkspaceEntryId(null);
    };

    const pager = usePagedList(collectionBuckets, 6, "notebookPage");

    const openNewEntry = (collection = null) => {
        setEditingEntry(null);
        setReturnCollectionId(collection?.isVirtual ? "__NONE__" : (collection?.id || null));
        setEntryModal(true);
        updateRouteState({ dialog: collection ? `entry-new:${collection.isVirtual ? "NONE" : collection.id}` : "entry-new" });
    };
    const openEditEntry = (entry, collection = null) => {
        setEditingEntry(entry);
        setReturnCollectionId(collection?.isVirtual ? "__NONE__" : (collection?.id || entry?.collectionId || null));
        setEntryModal(true);
        updateRouteState({ dialog: `entry:${entry.id}` });
    };
    const archiveCollection = async (collection) => {
        if (!await confirm.danger("Archivar colección", `Se archiva “${collection.name}” y sus registros.`, { confirmText: "Archivar" })) return;
        const res = await onApiAction({ method: "PATCH", endpoint: NOTEBOOK_COLLECTION_CONSTANTS.ARCHIVE_ONE, pathParams: { id: collection.id }, successMessage: "Colección archivada" });
        if (res.success) {
            CQ.mutateLocal({ id: collection.id, remove: true });
            entries.filter((entry) => String(entry.collectionId) === String(collection.id)).forEach((entry) => EQ.mutateLocal({ id: entry.id, remove: true }));
            if (String(advancedFilters.collectionId) === String(collection.id)) setAdvancedFilters((current) => ({ ...current, collectionId: "ALL" }));
        }
    };

    const clearFilters = () => {
        setSearch("");
        setAdvancedFilters(cloneAdvanced());
    };

    const exportRows = visible.map((e) => ({
        title: e.title || "", type: labelOf(NOTEBOOK_TYPES, e.type), status: labelOf(NOTEBOOK_STATUSES, e.status),
        collection: collectionMap.get(String(e.collectionId))?.name || "Sin colección", summary: e.summary || "",
        content: plain(e.content), author: e.author || "", tags: (e.tags || []).join(", "), words: e.wordCount || 0,
        genre: e.genre || "", mood: e.mood || "", musicalKey: e.musicalKey || "", bpm: e.bpm || "", updatedAt: e.updatedAt || "",
    }));

    const totalWords = entries.reduce((sum, entry) => sum + Number(entry.wordCount || 0), 0);
    const statistics = [
        { key: "entries", label: "Registros", value: entries.length, icon: <FaBook />, tone: "accent", isLoading: EQ.useApiLoading },
        { key: "collections", label: "Colecciones", value: collections.length, icon: <FaBookOpen />, tone: "neutral", isLoading: CQ.useApiLoading },
        { key: "pinned", label: "Fijados", value: entries.filter((entry) => entry.isPinned).length, icon: <FaThumbtack />, tone: "neutral", isLoading: EQ.useApiLoading },
        { key: "favorite", label: "Destacados", value: entries.filter((entry) => entry.isFavorite).length, icon: <FaStar />, tone: "neutral", isLoading: EQ.useApiLoading },
        { key: "finished", label: "Terminados", value: entries.filter((entry) => entry.status === "FINISHED").length, icon: <FaStar />, tone: "ok", isLoading: EQ.useApiLoading },
        { key: "words", label: "Palabras", value: totalWords.toLocaleString("es-AR"), icon: <FaFileAlt />, tone: "neutral", isLoading: EQ.useApiLoading },
    ];

    return <ViewLayout
        header={<PageHeader title="Cuaderno" icon={<FaBook />} description="Tu espacio privado para letras, música, libros, notas, ideas y escritura larga."
            actions={<>
                <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                <AppButton variant="outline" tone="neutral" icon={<FaFolderPlus />} onClick={() => { setEditingCollection(null); setCollectionModal(true); updateRouteState({ dialog: "collection-new" }); }}>Nueva colección</AppButton>
                <AppButton icon={<FaPlus />} onClick={openNewEntry}>Nuevo registro</AppButton>
            </>} />}
        toolbar={<ViewToolbar
            filters={<>
                <SearchFilter value={search} onChange={setSearch} total={entries.length} matches={visible.length} placeholder="Buscar en cualquier dato del registro..." />
                <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>
                    {advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}
                </AppButton>
            </>}
            expandedContent={<NotebookAdvancedFilterPanel isOpen={advancedOpen} value={advancedFilters}
                onChange={setAdvancedFilters} onClear={() => setAdvancedFilters(cloneAdvanced())} collections={collections} />}
            total={pager.total} totalSingular="colección" totalPlural="colecciones" perPage={pager.usePerPage} onChangePerPage={pager.onChangePerPage}
            exportColumns={[{key:"title",label:"Título"},{key:"type",label:"Tipo"},{key:"status",label:"Estado"},{key:"collection",label:"Colección"},{key:"summary",label:"Resumen"},{key:"content",label:"Contenido"},{key:"author",label:"Autor"},{key:"tags",label:"Etiquetas"},{key:"words",label:"Palabras"},{key:"genre",label:"Género"},{key:"mood",label:"Clima"},{key:"musicalKey",label:"Tonalidad"},{key:"bpm",label:"BPM"},{key:"updatedAt",label:"Actualizado"}]}
            exportRows={exportRows} exportName="cuaderno" />}
    >
        {!EQ.useApiLoading && visible.length === 0 && <EmptyState icon={<FaBook />} size="lg"
            title={entries.length ? "No hay coincidencias" : "Tu Cuaderno está vacío"}
            description={entries.length ? "Cambiá la búsqueda o los filtros avanzados para volver a ver registros." : "Creá una letra, nota, capítulo, idea o registro musical sin salir de NOTEBOOK."}
            action={entries.length
                ? <AppButton variant="outline" tone="neutral" onClick={clearFilters}>Limpiar filtros</AppButton>
                : <AppButton icon={<FaPlus />} onClick={openNewEntry}>Nuevo registro</AppButton>} />}

        {(EQ.useApiLoading || CQ.useApiLoading) && <div className="LStudio__notebook_grid"><Skeleton variant="box" height={180} count={3} /></div>}

        {!EQ.useApiLoading && !CQ.useApiLoading && collectionBuckets.length > 0 && <section className="LStudio__notebook_collections">
            <div className="LStudio__notebook_section_title"><div><strong>Colecciones</strong></div></div>
            <div className="LStudio__notebook_collection_grid">
                {pager.paged.map(({ collection: c, count }) => {
                    const openCollection = () => openWorkspace(c);
                    return <div key={c.isVirtual ? "__none__" : c.id} role="button" tabIndex={0} className="LStudio__notebook_collection_card"
                        data-virtual={c.isVirtual ? "true" : "false"}
                        data-active={(c.isVirtual && workspaceCollection?.isVirtual) || String(workspaceCollection?.id || "") === String(c.id || "")} onClick={openCollection}
                        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openCollection(); } }}
                        style={{ '--notebook-color': c.color || '#777' }}>
                        <span className="LStudio__notebook_collection_dot" /><div><strong>{c.name}</strong><small>{count} {count === 1 ? "registro" : "registros"}</small></div>
                        {!c.isVirtual && <span className="LStudio__notebook_collection_actions" onClick={(e) => e.stopPropagation()}>
                            <button type="button" title="Editar" onClick={() => { setEditingCollection(c); setCollectionModal(true); updateRouteState({ dialog: `collection:${c.id}` }); }}><FaPen /></button>
                            <button type="button" title="Archivar" onClick={() => archiveCollection(c)}><FaArchive /></button>
                        </span>}
                    </div>;
                })}
            </div>
        </section>}

        <ViewPagination page={pager.usePage} totalPages={pager.totalPages} total={pager.total} perPage={pager.usePerPage} onChange={pager.onChangePage} />

        <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)} title="Estadísticas · Cuaderno"
            description="Resumen general del contenido guardado en el Cuaderno." metrics={statistics} />
        <NotebookCollectionWorkspaceModal
            isOpen={!!workspaceCollection}
            collection={workspaceCollection}
            entries={hasActiveFiltering ? visible : entries}
            focusEntryId={workspaceEntryId}
            onFocusEntryHandled={() => setWorkspaceEntryId(null)}
            onClose={closeWorkspace}
            onNewEntry={(collection) => openNewEntry(collection)}
            onEditEntry={(entry, collection) => openEditEntry(entry, collection)}
            onEditCollection={(collection) => { setEditingCollection(collection); setCollectionModal(true); updateRouteState({ dialog: `collection:${collection.id}` }); }}
            isLoading={EQ.useApiLoading || CQ.useApiLoading}
        />
        <NotebookCollectionModal isOpen={collectionModal} collection={editingCollection} collections={collections} onClose={() => { dialogClosingRef.current = true; setCollectionModal(false); setEditingCollection(null); updateRouteState({ dialog: null }); }} onSaved={(saved, isEdit) => {
            CQ.mutateLocal(isEdit ? { id:saved.id, patch:saved } : { insert:saved });
            setEditingCollection(null);
            dialogClosingRef.current = true;
            updateRouteState({ dialog: null });
        }} />
        <NotebookEntryFormModal
            isOpen={entryModal}
            entry={editingEntry}
            collections={collections}
            entries={entries}
            defaultCollectionId={returnCollectionId === "__NONE__" ? "" : (returnCollectionId || (advancedFilters.collectionId !== "ALL" && advancedFilters.collectionId !== "NONE" ? advancedFilters.collectionId : ""))}
            onClose={() => { dialogClosingRef.current = true; setEntryModal(false); setEditingEntry(null); setReturnCollectionId(null); updateRouteState({ dialog: null }); }}
            onSaved={(saved,isEdit) => {
                if (!saved?.id) return;
                EQ.mutateLocal(isEdit ? { id:saved.id, patch:saved } : { insert:saved });
                setEditingEntry(null);
                dialogClosingRef.current = true;
                updateRouteState({ dialog: null });

                // Después de guardar seguimos al registro real, incluso si cambió de colección.
                if (!saved.collectionId) {
                    openWorkspace(ungroupedCollection, saved.id);
                } else {
                    const targetCollection = collections.find((item) => String(item.id) === String(saved.collectionId));
                    if (targetCollection) openWorkspace(targetCollection, saved.id);
                }
                setReturnCollectionId(null);
            }}
        />
    </ViewLayout>;
};
